/** This is for Form initiation */
import { getLastPageUrl } from "@utils/pageTracker";

/**
 * Checks if any of the specified form elements have been interacted with
 * @param {Array} formElements - Array of form element names to check
 * @returns {boolean} - True if any of the elements have been interacted with, false otherwise
 */

const isFormInteracted = (form) => {
  return new Promise((resolve) => {
	  if (typeof window === 'undefined') {
		  resolve(false);
		  return;
	  }
	  const formElements = 'input, .form-field[role="combobox"], textarea, form button';

    if (form) {
      // Use event delegation by attaching listener to the form
      form.addEventListener('focusin', (event) => {
        const target = event.target;
		  if (target.matches(formElements)) {
          resolve(true);
        }
	  }, true);
    }
  });
};


const GTM = {

  // Global Page Level Data Layer
  pageView: (pageInfo = {}, siteInfo = {}) => {
		if (typeof window !== 'undefined' && "dataLayer" in window) {
      window.dataLayer.push({
        event: "page_view",
        pageInfo: {
			pageName: window.location.hash !== '' ? window.location.pathname + window.location.hash : window.location.pathname?.replace('/', '')?.replaceAll('-', ' '),
			pageURL: window.location.href || "",
			section: window.location.pathname?.replace('/', '')?.replaceAll('-', ' ') || "",
			subsection: "not_available",
			language: pageInfo.language || "en-US",
			country: "US"
        },
        siteInfo: {
          clientName: "pacira",
          siteName: siteInfo.siteName || "exparelpro",
          siteCategory: siteInfo.siteCategory || "hcp"
        }
	  });
		}
	},

	errorPage: (code) => {
		if (typeof window !== 'undefined' && "dataLayer" in window) {
			window.dataLayer.push({
				event: "error_page_load",
				eventInfo: {
					httpCode: code,
					PagePathwithQueryStrings: window.location.href
				}
			});
		}
	},


  // form tags START
	formInitiation: async (formName) => {
		if (typeof window !== 'undefined' && "dataLayer" in window) {
		const form = document.querySelector(`form`);
		const formInteracted = await isFormInteracted(form);
		if (formInteracted) {
			window.dataLayer.push({
				event: "form_initiation",
				eventInfo: {
					formName,
					label: "FormInitiated"
				}
			});
		}
    }
	},

	fieldCompletion: (formName) => {
		if (typeof window !== 'undefined' && "dataLayer" in window && !window.__formTrackingInitialized) {
			const form = document.querySelector(`form`);

			if (form) {
				form.addEventListener('blur', function (event) {
					const target = event.target;

					if (
						['INPUT', 'TEXTAREA'].includes(target.tagName) &&
						!['checkbox', 'radio'].includes(target.type) &&
						target.value.trim().length > 0
					) {
						const name = target.name || target.id || 'unknown';

						const restrictedFields = ['firstName', 'middleName', 'lastName', 'email', 'npiNumber', 'address', 'city', 'zipCode', 'phoneNumber', 'message'];
						const isRestricted = restrictedFields.some(restricted => name.toLowerCase().includes(restricted.toLowerCase()));

			// For restricted fields, only send the event without the field value
						window.dataLayer.push({
							event: 'field_completion',
							eventInfo: {
								formName,
								fieldName: name,
								...(isRestricted ? {fieldValue:"Complete"} : { fieldValue: target.value })
							}
						});
					}
				}, true);

				form.addEventListener('change', function (event) {
					const target = event.target;

					if (target.tagName === 'SELECT') {
						const name = target.name || target.id || 'unknown';

						window.dataLayer.push({
							event: 'field_completion',
							eventInfo: {
								formName,
								fieldName: name,
								fieldValue: target.value
							}
						});
					}
				});

				const trackedRadios = new Set();

				document.addEventListener('change', function (event) {
					if (['checkbox', 'radio'].includes(event.target.type)) {
						trackedRadios.add(event.target);
					}
				});

				document.addEventListener('blur', function (event) {
					if (trackedRadios.has(event.target)) {
						trackedRadios.delete(event.target);

						// Push to dataLayer only now — on completion
						window.dataLayer.push({
							event: 'field_completion',
							eventInfo: {
								formName,
								fieldName: event.target.name || event.target.id || 'unknown',
								fieldValue: event.target.value
							}
						});
					}
				}, true);
			}
			window.__formTrackingInitialized = true;
		}
	},

	formDelivery: (formName, interstitialName = "") => {
		if (typeof window !== 'undefined' && "dataLayer" in window) {
      window.dataLayer.push({
		  event: "form_delivery",
		  eventInfo: {
			  formName,
			  label: interstitialName // Will be empty string if not loaded in interstitial
		  }
      });
    }
	},


	fieldValidation: (formName, fieldName, fieldError) => {
		if (typeof window !== 'undefined' && "dataLayer" in window) {
			window.dataLayer.push({
				event: "validation_errors",
				eventInfo: {
					formName,
					fieldName,
					fieldError
				}
			});
		}
	},

	formSubmission: (formName) => {
		if (typeof window !== 'undefined' && "dataLayer" in window) {
			window.dataLayer.push({ event: "form_submissions", eventInfo: { formName, label: 'Complete' } });
		}
	},

	formSubmissionError: (formName) => {
		if (typeof window !== 'undefined' && "dataLayer" in window) {
			window.dataLayer.push({ event: "validation_errors", eventInfo: { formName, label: 'Error' } });
		}
	},

	trackCTA: () => {
		if (typeof window !== 'undefined' && "dataLayer" in window && !window.__ctaTrackingInitialized) {
			document.addEventListener('click', (event) => {
				if (event.target.closest('a, button')) {
					if (event.target.href?.toLowerCase().includes('/connect')) {
						window.dataLayer.push({
							event: "cta_click",
							eventInfo: {
								currentPage : getLastPageUrl(),
								destinationURL: event.target.href,
								linkText: event.target.text,
								location: "Body"
							}
						});
					} else if (event.target.closest('header')) {
						if (event.target.closest('.gtm-header')) {
							window.dataLayer.push({
								event: "navigation_click",
								eventInfo: {
									currentPage : getLastPageUrl(),
									destinationURL: event.target.href,
									position: "Header",
									linkText: event.target.textContent || event.target.innerText
								}
							});
						} else if (event.target.closest('.gtm-nav')) {
							window.dataLayer.push({
								event: "navigation_click",
								eventInfo: {
									currentPage : getLastPageUrl(),
									destinationURL: event.target.href,
									position: "Navbar",
									linkText: event.target.textContent || event.target.innerText
								}
							});
						}
					} else if (event.target.closest('footer')) {
						window.dataLayer.push({
							event: "navigation_click",
							eventInfo: {
								currentPage : getLastPageUrl(),
								destinationURL: event.target.href,
								position: "Footer",
								linkText: event.target.textContent || event.target.innerText
							}
						});
					} else if (event.target.closest('#entry-modal') && event.target.classList.contains('dissmiss')) {
						window.dataLayer.push({
							event: "interstitial_dismiss",
							eventInfo: {
								linkText: event.target.text,
								label: "HCPSiteEntry"
							}
						});
					} else if (event.target.closest('.carousel__controls ')) {
						let linkText = document.querySelector('.cascade-slider_container .now h4').textContent;
						let title = '';

						if (event.target.closest('.cascade-slider_arrow-right') || event.target.closest('.cascade-slider_arrow-left')) {
							if (event.target.closest('.cascade-slider_arrow-right')) {
								title = document.querySelector('.cascade-slider_container .prev h4').textContent;
							} else if (event.target.closest('.cascade-slider_arrow-left')) {
								title = document.querySelector('.cascade-slider_container .next h4').textContent;
							}
							window.dataLayer.push({
								"event": "carousel-click",
								"eventInfo": {
									"label": title,
									"linkText": linkText
								}
							});
						}
					}

					else {
						if (!event.target.closest('.watch-video') && !event.target.closest('.isi') && !event.target.closest('.sticky-isi') && !event.target.closest('form') && !event.target.closest('.accordion-button') && !event.target.closest('.event-link-btn') && !event.target.closest('.ref-toggle')) {
							window.dataLayer.push({
								event: "cta_click",
								eventInfo: {
									currentPage : getLastPageUrl(),
									destinationURL: event.target.href || "",
									linkText: event.target.textContent || event.target.innerText,
									location: "Body"
								}
							});
						}
					}

					if (event.target.href && (event.target.href.startsWith('http') || event.target.href.startsWith('www')) && !event.target.href.includes(window.location.hostname) && !event.target.closest('header') && !event.target.closest('.footer')) {
						window.dataLayer.push({
							event: "outbound_links",
							eventInfo: {
								currentPage : getLastPageUrl(),
								destinationURL: event.target.href,
								label: event.target.text
							}
						});
					}

					if (event.target.closest('.event-link-btn')) {
						window.dataLayer.push({
							event: "outbound_links",
							eventInfo: {
								destinationURL: event.target.getAttribute('data-link'),
								label: event.target.innerText
							}
						});
					}
				}

				if (event.target.closest('.ortho-clickable-cards .card4')){
					window.dataLayer.push({
						event: "cta_click",
						eventInfo: {
							destinationURL: event.target.closest('.card4').id || "",
							linkText: event.target.closest('.card4').textContent,
							location: "Body"
						}
					});
				}

				if (event.target.closest('.tabs-header')) {
					window.dataLayer.push({
						event: "tab-click",
						eventInfo: {
							loadType: 'click',
							linkText: event.target.textContent
						}
					});
				}

				if (event.target.closest('.ref-toggle')){
					
					const isExpanded = event.target.closest('.ref-toggle').getAttribute('aria-expanded') === 'true';
          const action = isExpanded ? 'Expand' : 'Collapse';
					
          window.dataLayer.push({
            event: "expansions",
            eventInfo: {
              action,
              linkText: 'reference'
            }
          });
				}
			});
			window.__ctaTrackingInitialized = true;
		}
	},

	trackAccordion: () => {
    if (typeof window !== 'undefined' && "dataLayer" in window && !window.__accordionTrackingInitialized) {
      document.addEventListener('click', (event) => {
        const accordionButton = event.target.closest('.accordion-button');
        if (accordionButton) {
					
          const isExpanded = accordionButton.getAttribute('aria-expanded') === 'true';
          const action = isExpanded ? 'Expand' : 'Collapse';
          const label = accordionButton.querySelector('span')?.textContent?.trim() || '';
					
          window.dataLayer.push({
            event: "expansions",
            eventInfo: {
              action,
              linkText: label
            }
          });
        }
      });
			window.__accordionTrackingInitialized = true;
		}
	},

	carouselClick: (event, index = 0) => {
		if (typeof window !== 'undefined' && "dataLayer" in window) {
			let title = document.querySelector('.cascade-slider_container .now h4').textContent;
			let linkText = '';
			if (event.target.closest('.prev')) {
				linkText = document.querySelector('.cascade-slider_container .prev h4').textContent;
			} else if (event.target.closest('.next')) {
				linkText = document.querySelector('.cascade-slider_container .next h4').textContent;
			} else if (event.target.closest('.carousel__dots')) {
				linkText = document.querySelector('.cascade-slider_container > div > div:nth-child(' + (index + 1) + ') h4').textContent;
			}
			window.dataLayer.push({
				"event": "carousel-click",
				"eventInfo": {
					"label": title,
					"linkText": linkText
				}
			});
		}
	},

	trackOpenModal: (modalName) => {
		if (typeof window !== 'undefined' && "dataLayer" in window) {
			window.dataLayer.push({
				event: "interstitial_load",
				eventInfo: {
					label: modalName
				}
			});
		}
	},

	trackCloseModal: (linkText, label) => {
		if (typeof window !== 'undefined' && "dataLayer" in window) {
			window.dataLayer.push({
				event: "interstitial_dismiss",
				eventInfo: {
					linkText,
					label
				}
			});
		}
	},

	isiClick: (linkText, action) => {
		if (typeof window !== 'undefined' && "dataLayer" in window) {
			window.dataLayer.push({
				event: "expansions",
				eventInfo: {
					linkText,
					action
				}
			});
		}
	},
  // form tags END

  // Video tracking START
  videoStart: (title) => {
	  if (typeof window !== 'undefined' && "dataLayer" in window) {
      window.dataLayer.push({
		  event: "video_plays",
		  eventInfo: {
			  videoTitle: title,
			  label: "Start"
		  }
      });
    }
  },

  videoEnd: (title) => {
	  if (typeof window !== 'undefined' && "dataLayer" in window) {
      window.dataLayer.push({
		  event: "video_ends",
		  eventInfo: {
			  videoTitle: title,
			  label: "Complete"
		  }
      });
    }
  },


  reset: () => {
	  if (typeof window !== 'undefined' && "dataLayer" in window) {
		  window.dataLayer = [];
	  }
  },
};

export default GTM;
